import clsx from 'clsx';
import type * as React from 'react';

type SkeletonShape = 'rect' | 'circle';

/**
 * A skeleton to show loading state in place of content (text, icon...).
 *
 * Shape : rectangle or circle
 * Size :  can be set using tailwind classes
 * Animation : shimmer
 *
 * @example
 * ```tsx
 * <Skeleton>
 *   This is a loading text
 * </Skeleton>
 * ```
 */
export function Skeleton(
    props: React.ComponentPropsWithoutRef<'div'> & { shape?: SkeletonShape; className?: string }
) {
    const { className, shape = 'rect', children, ...rest } = props;

    return (
        <div
            className={clsx(
                'shimmer text-transparent',
                // If using the `circle` shape you need to specify
                // the circle size through classes (ie: size-10).
                shape === 'circle' ? 'rounded-full' : 'rounded',
                className
            )}
            aria-busy
            aria-hidden
            {...rest}
        >
            {children}
        </div>
    );
}

export function IconSkeleton(props: { size?: number; className?: string }) {
    return (
        <Skeleton
            className={props.className}
            shape="circle"
            style={{
                width: props.size,
                height: props.size,
            }}
        />
    );
}

export type TextSkeletonProps = {
    className?: string;
    children?: React.ReactNode;
};

/**
 * A skeleton for text content.
 */
export function TextSkeleton(props: TextSkeletonProps) {
    return (
        <div aria-busy className={clsx('relative text-transparent', props.className)}>
            <div
                aria-hidden
                className={clsx(
                    'relative inline-block h-[1lh] whitespace-nowrap',
                    !props.children && 'w-full'
                )}
            >
                <Skeleton className="absolute top-[0.1lh] right-0 left-0 h-[0.8lh]" />
                {props.children}
            </div>
        </div>
    );
}
